// Página: Iluminação
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { getWhatsAppLink } from "../components/whatsapp/getWhatsAppLink";
import { Link } from "react-router-dom";
import GalleryCarousel from "../components/portfolio/GalleryCarousel";
import Lightbox from "../components/portfolio/Lightbox";
import { portfolioMedia } from "../data/portfolioMedia";

function Iluminacao() {
  const title = "Iluminação cênica para casamentos e eventos | Cenovix";
  const description =
    "Iluminação cênica premium para casamentos e eventos. Atmosfera aconchegante, qualidade da luz e resultado em foto e vídeo. Montagem completa e opção de técnico durante o evento.";
  const url = "https://www.cenovix.com.br/iluminacao";
  const ogImage = "https://www.cenovix.com.br/images/og-cenovix.jpg";

  const waLink = getWhatsAppLink("/iluminacao");

  const [videoLbOpen, setVideoLbOpen] = useState(false);

  // Vídeo
  const videoSrc = "/videos/iluminacao/01.mp4";
  const videoPoster =
    "/images/portfolio/posters/" +
    "iluminacao-video.jpeg";

  const videoLbItems = [{ src: videoSrc, poster: videoPoster, type: "video" }];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Iluminação cênica para casamentos e eventos",
    description,
    provider: {
      "@type": "LocalBusiness",
      name: "Cenovix",
      url: "https://www.cenovix.com.br/",
      telephone: "+55 27 99578-5444",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Vila Velha",
        addressRegion: "ES",
        addressCountry: "BR",
      },
      sameAs: ["https://www.instagram.com/ceno.vix"],
    },
    areaServed: [
      { "@type": "AdministrativeArea", name: "Espírito Santo" },
      { "@type": "Country", name: "Brasil" },
    ],
    serviceType: [
      "Iluminação cênica",
      "Iluminação para eventos",
      "Iluminação para casamentos",
    ],
    url,
  };

  return (
    <div className="iluminacao">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index,follow,max-image-preview:large" />

        {/* Compartilhar */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cenovix" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={ogImage} />
        <meta
          property="og:image:alt"
          content="Iluminação cênica Cenovix — atmosfera e destaque"
        />

        {/* Compartilhar */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />

        <meta name="theme-color" content="#0b0b0c" />

        {/* Dados estruturados */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Topo */}
      <section className="iluminacao-hero" aria-label="Iluminação cênica Cenovix">
        <h1>
          <span className="only-desktop">
            Iluminação que cria atmosfera e valoriza cada detalhe
          </span>

          <span className="only-mobile">
            Iluminação que cria atmosfera
            <br />
            e valoriza o evento
          </span>
        </h1>

        <p className="hero-subtitle">
          Para <strong>casamentos</strong> e{" "}
          <strong>celebrações especiais</strong>, desenvolvemos uma iluminação{" "}
          <strong>aconchegante</strong>, <strong>elegante</strong> e{" "}
          <strong>bem equilibrada</strong>, pensada para o ambiente, para as
          pessoas e para como tudo será lembrado em <strong>foto e vídeo</strong>
          .
          <br />
          <span style={{ opacity: 0.9 }}>
            Localizados em <strong>Vila Velha</strong>, atendemos todo{" "}
            <strong>Brasil</strong>.
          </span>
        </p>

        <div className="hero-stats" aria-label="Destaques Cenovix Iluminação">
          <div className="stat">
            <span className="stat-number">Atmosfera</span>
            <span className="stat-label">aconchegante</span>
          </div>

          <div className="stat">
            <span className="stat-number">Qualidade</span>
            <span className="stat-label">da luz</span>
          </div>

          <div className="stat">
            <span className="stat-number">Memórias</span>
            <span className="stat-label">bem iluminadas</span>
          </div>
        </div>
      </section>

      {/* Vídeo */}
      <section aria-label="Vídeo de iluminação">
        <h2>Vídeo do que podemos fazer no seu evento</h2>
        <p>
          Um vídeo curto mostra melhor a <strong>atmosfera</strong>, a{" "}
          <strong>qualidade da luz</strong> e a <strong>experiência real</strong>{" "}
          dos nossos projetos.
        </p>

        <div
          className="video-card"
          role="button"
          tabIndex={0}
          aria-label="Abrir vídeo em tela cheia"
          onClick={() => setVideoLbOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setVideoLbOpen(true);
            }
          }}
        >
          <video
            className="video-card__media"
            src={videoSrc}
            poster={videoPoster}
            playsInline
            preload="metadata"
          />
        </div>
      </section>

      {/* Provas visuais */}
      <section
        className="visual-editorial"
        aria-label="Projetos de iluminação cênica"
      >
        {portfolioMedia.iluminacao && portfolioMedia.iluminacao.length > 0 ? (
          <>
            <GalleryCarousel
              items={portfolioMedia.iluminacao}
              ariaLabel="Galeria de iluminação — carrossel"
            />

            <div style={{ marginTop: 18 }}>
              <Link
                to="/portfolio#iluminacao"
                className="btn-portfolio"
                aria-label="Ver mais projetos de iluminação no portfólio"
              >
                Veja mais no portfólio
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="visual-editorial__grid">
              <figure className="visual-tile visual-tile--big">
                <img
                  src="/images/portfolio/iluminacao/01.jpg"
                  alt="Iluminação cênica Cenovix em evento: atmosfera e destaque"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="visual-caption">
                  Atmosfera que valoriza o evento
                </figcaption>
              </figure>

              <div className="visual-editorial__stack">
                <figure className="visual-tile">
                  <img
                    src="/images/portfolio/iluminacao/02.jpg"
                    alt="Iluminação Cenovix: qualidade visual e conforto para os convidados"
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="visual-caption">
                    Qualidade visual e conforto
                  </figcaption>
                </figure>

                <figure className="visual-tile">
                  <img
                    src="/images/portfolio/iluminacao/03.jpg"
                    alt="Iluminação Cenovix: resultado pensado para foto e vídeo"
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="visual-caption">
                    Resultado pensado para foto e vídeo
                  </figcaption>
                </figure>
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <Link
                to="/portfolio#iluminacao"
                className="btn-portfolio"
                aria-label="Ver mais projetos de iluminação no portfólio"
              >
                Veja mais no portfólio
              </Link>
            </div>
          </>
        )}
      </section>

      {/* O que entregamos */}
      <section>
        <h2>O que a Cenovix entrega em iluminação</h2>

        <div className="solution-cards">
          <div className="solution-card">
            <h3>Atmosfera</h3>
            <p>
              A iluminação certa muda completamente a sensação do espaço. Criamos
              ambientes acolhedores, elegantes e agradáveis aos olhos, onde as
              pessoas se sentem confortáveis e presentes.
            </p>
          </div>

          <div className="solution-card">
            <h3>Qualidade visual</h3>
            <p>
              Trabalhamos a luz para valorizar cores, volumes e texturas,
              evitando excessos, sombras duras ou desconforto visual — tudo com
              equilíbrio.
            </p>
          </div>

          <div className="solution-card">
            <h3>Execução tranquila</h3>
            <p>
              Cuidamos de toda a montagem e dos ajustes necessários. Quando
              solicitado, um <strong>técnico acompanha o evento</strong> para
              garantir que tudo aconteça como planejado.
            </p>
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section>
        <h2>Como funciona</h2>

        <div
          className="process-flow"
          role="list"
          aria-label="Processo de iluminação Cenovix"
        >
          <div className="process-step" role="listitem">
            <div className="process-step__content">
              <h3>Briefing</h3>
              <p>Data, local, estilo e referências.</p>
            </div>
          </div>

          <div className="process-arrow" aria-hidden="true">
            →
          </div>

          <div className="process-step" role="listitem">
            <div className="process-step__content">
              <h3>Conceito</h3>
              <p>Atmosfera desejada e pontos de destaque.</p>
            </div>
          </div>

          <div className="process-arrow" aria-hidden="true">
            →
          </div>

          <div className="process-step" role="listitem">
            <div className="process-step__content">
              <h3>Montagem</h3>
              <p>Execução, testes e ajustes no local.</p>
            </div>
          </div>

          <div className="process-arrow" aria-hidden="true">
            →
          </div>

          <div className="process-step" role="listitem">
            <div className="process-step__content">
              <h3>Entrega</h3>
              <p>Evento entregue com excelência.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Onde atuamos */}
      <section aria-label="Onde atuamos">
        <h2>Onde atuamos</h2>

        <div className="solution-cards">
          <div className="solution-card">
            <h3>Vila Velha • Vitória • Grande Vitória</h3>
            <p>
              Atuamos com cenografia e iluminação cênica em Vila Velha, Vitória e
              Grande Vitória, atendendo eventos corporativos e sociais em todo o
              Espírito Santo.
            </p>
          </div>
        </div>
      </section>

      {/* WhatsApp */}
      <section>
        <h2>Vamos cuidar da iluminação do seu evento?</h2>

        <p>
          Envie no WhatsApp a <strong>data</strong>, o <strong>local</strong>, o{" "}
          <strong>tipo de celebração</strong> e o <strong>estilo</strong> que
          você quer. A partir disso, pensamos na atmosfera, na qualidade da luz
          e nos detalhes.
        </p>

        <a
          className="cta"
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Falar no WhatsApp"
        >
          Falar no WhatsApp
        </a>
      </section>

      <Lightbox
        open={videoLbOpen}
        items={videoLbItems}
        index={0}
        onClose={() => setVideoLbOpen(false)}
      />

      {/* CSS local */}
      <style>{`
        .only-mobile { display: none; }
        .only-desktop { display: inline; }

        @media (max-width: 900px) {
          .only-desktop { display: none; }
          .only-mobile { display: inline; }
        }
      `}</style>
    </div>
  );
}

export default Iluminacao;
